import { Inject, Injectable, Injector, NgZone } from "@angular/core";
import { AppMetaData, AppMetaDataItemNames, CourseEntity, CourseLessonEntity, CourseLessonProgressEntity, CourseLessonSessionEntity, CourseModuleProgressEntity, LessonTypeAllItems, StudentCourseEntity, VideoEntity, VideoLibraryEntity, VideoStreamingProviderEntity } from "../../../meta-data/app-meta-data.service";
import { DevwareMetaData, DwCacheService, DwConfigService, DwConfigServiceToken, DwEventService, DwLoggingService, DwMetaDataService, DwMetaDataServiceToken, DwOrmData, DwOrmDataService, DwOrmDataServiceToken, DwSecurityTopics, DwSecurityUserService, areEqual, deepCopy } from "@devwareapps/devware-cap";
import { BehaviorSubject, Observable, Subject, combineLatest, of } from "rxjs";
import { Router } from "@angular/router";
import { map } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { DwPubSubMessage } from "./models/dw-pub-sub-message.model";
import { DwClientSessionInfo } from "./models/dw-client-session-info.model";
import { DwClientSessionUtilService } from "./dw-client-session-util.service";
import { VideoRepositoryService } from "../../videos/services/video-repository.service";
import { DwPubSubEventTopics } from "./models/dw-pub-sub-event-topics.model";
import { DwPubSubDebugEvent, DwPubSubDebugSnapshot } from "./models/dw-pub-sub-debug-event.model";

@Injectable({ providedIn: 'root' })
export class DwPubSubService {
    //private static videoBroadcastChannel: BroadcastChannel = null;
    apiRoot: string;

    private pubSubConnectionSetup: boolean = false;
    private pubSubConnectionSettingUp: boolean = false;
    private socket?: WebSocket;
    private userId?: number;
    private clientSessionInfo?: DwClientSessionInfo;
    pubSubClientUrl: string = '';
    openAttempt: number = 0;

    constructor(private dwEventService: DwEventService, private dwLoggingService: DwLoggingService, private dwSecurityUserService: DwSecurityUserService, private injector: Injector, private zone: NgZone, @Inject(DwConfigServiceToken) private dwConfigService: DwConfigService) {
        this.apiRoot = this.dwConfigService.coreConfig.apiRoot;

        this.setupPubSubConnection();

        this.dwSecurityUserService.securityContextChanged().subscribe(() => {
            this.setupPubSubConnection();
        });
    }

    setupPubSubConnection() {
        const securityContext = this.dwSecurityUserService.securityContext;
        const user = this.dwSecurityUserService.securityContext?.ApplicationUser;
        const userId = this.dwSecurityUserService.securityContext?.ApplicationUser?.UserId || 0;

        // If not logged in, we don't create a session and close out an existing one
        if (!userId || userId == 0) {
            this.closeSession();
            return;
        }

        // Ensure that something has changed before restarting the connection
        if (this.clientSessionInfo?.UserId == userId && (this.pubSubConnectionSetup || this.pubSubConnectionSettingUp)) {
            return;
        }

        this.clientSessionInfo = DwClientSessionUtilService.buildSessionInfo(securityContext, 1);

        this.pubSubConnectionSettingUp = true;

        // Get the PubSub client URL and start the connection
        this.getPubSubClientUrl(userId)
            .subscribe(url => {
                this.pubSubClientUrl = url;
                this.startWebSocketConnection(url, userId);

                this.pubSubConnectionSettingUp = false;
                this.pubSubConnectionSetup = true;
            });
    }

    closeSession() {

        try {

            if (this.socket) {
                this.socket.close();
            }
        } catch (error) {
            this.dwLoggingService.logError('Error closing PubSub WebSocket connection', error, null, true);
        }
    }

    getPubSubClientUrl(userId): Observable<string> {

        if (!userId) {
            return of('');
        }
        
        const url = `${this.apiRoot}/aviator-online/pub-sub/negotiate?userId=${userId}`;
        
        const httpClient = this.injector.get(HttpClient);

        return httpClient.get<string>(url);
    }

    publishBroadcastMessage(message: DwPubSubMessage): void {
         const ts = new Date().toISOString();
        this.patchSnapshot({ LastSendUtc: ts });
        this.emitDebug('Send', message.Topic, {detail: `Message sent to all users`, ...message});

        message.FromClient = this.clientSessionInfo?.SessionId;

        const url = `${this.apiRoot}/aviator-online/pub-sub/broadcast`;

        const httpClient = this.injector.get(HttpClient);
        httpClient.post(url, message).subscribe();
    }

    publishMessageToUser(userId: number, message: DwPubSubMessage, ignoreDebug: boolean = false): void {
        if (!ignoreDebug) {
            const ts = new Date().toISOString();
            this.patchSnapshot({ LastSendUtc: ts });
            this.emitDebug('Send', message.Topic, {detail: `Message sent to user id ${userId}`, ...message});
        }

        message.FromClient = this.clientSessionInfo?.SessionId;

        const url = `${this.apiRoot}/aviator-online/pub-sub/user/${userId}`;

        const httpClient = this.injector.get(HttpClient);
        httpClient.post(url, message).subscribe();
    }

    publishMessageToCurrentUser(message: DwPubSubMessage): void {
        const currentUser = this.dwSecurityUserService?.securityContext?.ApplicationUser;

        if (!currentUser) {
            return;
        }

        const ts = new Date().toISOString();
        this.patchSnapshot({ LastSendUtc: ts });
        this.emitDebug('Send', message.Topic, {detail: `Message sent to other browsers for ${currentUser.FirstNameLastName} (${currentUser.UserId})`, ...message});
        
        this.publishMessageToUser(currentUser.UserId, message, true);
    }


    startWebSocketConnection(url: string, userId: number) {
        try {

            // Don't setup the same user more than once
            if (this.userId == userId) {
                return;
            }

            this.userId = userId;

            this.closeSession();

            const clientInfoString = this.clientSessionInfo?.ClientInfoString || '';

            this.socket = new WebSocket(url);

             this.openAttempt += 1;
            this.patchSnapshot({ State: 'Connecting', Attempt: this.openAttempt, SessionInfo: this.clientSessionInfo, ConnectedUrl: url });

            this.socket.onopen = (event: Event) => {
                this.dwEventService.publishEvent(DwPubSubEventTopics.clientConnected, event);

               

                this.patchSnapshot({ State: 'Open', Attempt: this.openAttempt, SessionInfo: this.clientSessionInfo, ConnectedUrl: url });
                this.emitDebug('State', 'Open', { attempt: this.openAttempt, url, sessionInfo: this.clientSessionInfo });
            };

            this.socket.onclose = (event: CloseEvent) => {
                this.dwEventService.publishEvent(DwPubSubEventTopics.clientDisconnected, event);
                const ts = new Date().toISOString();

                this.patchSnapshot({ State: 'Closed', LastCloseUtc: ts });
                this.emitDebug('State', 'Closed', { event });
            };

            this.socket.onerror = (event: ErrorEvent) => {
                this.dwEventService.publishEvent(DwPubSubEventTopics.clientError, event);

                const ts = new Date().toISOString();
                this.patchSnapshot({ State: 'Error', LastErrorUtc: ts });
                this.emitDebug('Error', 'Socket error', safeSerializeError(event.error));
                this.dwLoggingService.logWarning('PubSub WebSocket error', {
                    userId,
                    clientInfoString,
                    error: safeSerializeError(event.error)
                }, true);
            };


            this.socket.onmessage = (event: MessageEvent<string>) => {
                this.zone.run(() => {
                   // console.log('Global Message Received:', event.data);

                    this.handleGlobalMessage(event.data);
                });
            };
        } catch (error) {
            this.dwLoggingService.logError('Error in startWebSocketConnection', error, null, true);
        }
    }


    handleGlobalMessage(data: string) {
        const message = JSON.parse(data) as DwPubSubMessage;

        if (message.FromClient === this.clientSessionInfo?.SessionId) {
            return;
        }

        const ts = new Date().toISOString();
        this.patchSnapshot({ LastSendUtc: ts });
        this.emitDebug('Received', message.Topic, {detail: 'Message received', ...message});

        this.dwEventService.publishEvent(DwPubSubEventTopics.messageReceived(message.Topic), message);
    }

    private _debugEvents = new Subject<DwPubSubDebugEvent>();
    public debugEvents$ = this._debugEvents.asObservable();

    private _debugSnapshot = new BehaviorSubject<DwPubSubDebugSnapshot>({
        State: 'Idle',
        Attempt: 0
    });
    public debugSnapshot$ = this._debugSnapshot.asObservable();

    // Optional: store last N events for late subscribers / screenshots
    private _buffer: DwPubSubDebugEvent[] = [];
    private readonly _bufferMax = 500;

    /** Call this whenever you have a new detail worth logging */
    emitDebug(kind: DwPubSubDebugEvent['Kind'], label: string, data?: any) {
        const evt: DwPubSubDebugEvent = {
            Id: DwClientSessionUtilService.generateUuid(),
            TsUtc: new Date().toISOString(),
            Kind: kind,
            Label: label,
            Data: data
        };

        this._buffer.push(evt);
        if (this._buffer.length > this._bufferMax) this._buffer.shift();

        this._debugEvents.next(evt);
    }

    /** Expose current buffered events (for monitor init) */
    public getDebugBuffer(): DwPubSubDebugEvent[] {
        return [...this._buffer];
    }

    /** Clear the buffer */
    public clearDebugBuffer(): void {
        this._buffer = [];
        this.emitDebug('Info', 'Debug buffer cleared');
    }

    /** Update snapshot helper */
    private patchSnapshot(patch: Partial<DwPubSubDebugSnapshot>) {
        this._debugSnapshot.next({ ...this._debugSnapshot.value, ...patch });
    }


}


function safeSerializeError(err: any) {
    if (!err) return err;
    try {
        if (err instanceof Error) return { name: err.name, message: err.message, stack: err.stack };
        // Avoid circular structures
        return JSON.parse(JSON.stringify(err));
    } catch {
        return { message: String(err) };
    }
}
